/**
 * Utility functions for handling TimeSpan conversion between .NET and HTML formats
 */

/**
 * Converts ISO 8601 duration format (PT11H, PT8H30M, PT30M) to HTML time input format (HH:mm)
 * @param isoDuration - ISO 8601 duration string (e.g., "PT11H", "PT8H30M", "PT30M")
 * @returns HTML time format string (e.g., "11:00", "08:30", "00:30") or empty string if invalid
 */
export const convertISODurationToTimeInput = (isoDuration: string | null | undefined): string => {
  if (!isoDuration) return '';
  
  try {
    // Match patterns like PT11H, PT8H30M, PT30M, PT1H45M
    const match = isoDuration.match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/);
    
    if (!match) return '';
    
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    
    // Validate ranges
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return '';
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting ISO duration to time input:', error);
    return '';
  }
};

/**
 * Converts HTML time input format (HH:mm) to ISO 8601 duration format (PTxHxM)
 * @param timeInput - HTML time format string (e.g., "11:00", "08:30")
 * @returns ISO 8601 duration string (e.g., "PT11H", "PT8H30M") or null if invalid
 */
export const convertTimeInputToISODuration = (timeInput: string | null | undefined): string | null => {
  if (!timeInput) return null;
  
  try {
    const match = timeInput.match(/^(\d{1,2}):(\d{2})$/);
    
    if (!match) return null;
    
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    
    // Validate ranges
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    
    let duration = 'PT';
    
    if (hours > 0) {
      duration += `${hours}H`;
    }
    
    if (minutes > 0) {
      duration += `${minutes}M`;
    }
    
    // If both are 0, return PT0M
    if (hours === 0 && minutes === 0) {
      duration = 'PT0M';
    }
    
    return duration;
  } catch (error) {
    console.error('Error converting time input to ISO duration:', error);
    return null;
  }
};

/**
 * Formats ISO 8601 duration for display purposes
 * @param isoDuration - ISO 8601 duration string (e.g., "PT11H", "PT8H30M", "PT30M")
 * @returns Formatted display string (e.g., "11:00", "08:30", "00:30") or "-" if invalid
 */
export const formatCutoffTimeForDisplay = (isoDuration: string | null | undefined): string => {
  const converted = convertISODurationToTimeInput(isoDuration);
  return converted || '-';
};
